import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import { UiPanelComponent } from '@template-ui/components';
import { UiButtonDirective } from '@template-ui/directives';

export interface WorkflowDesignerOutlineEntry {
  readonly key: string;
  readonly nodeId: string | null;
  readonly label: string;
  readonly typeLabel: string;
  readonly icon: string;
  readonly issueCount: number;
  readonly inspectable: boolean;
}

/** Process selection only; the workspace owns navigation, guards and the draft. */
@Component({
  selector: 'phms-workflow-designer-step-outline',
  imports: [CommonModule, UiPanelComponent, UiButtonDirective],
  templateUrl: './workflow-designer-step-outline.component.html',
  styleUrl: './workflow-designer-step-outline.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.workflow-step-outline--collapsed]': '!expanded && !drawerOpen',
  },
})
export class WorkflowDesignerStepOutlineComponent {
  @Input() entries: readonly WorkflowDesignerOutlineEntry[] = [];
  @Input() selectedNodeId: string | null = null;
  @Input() blocked = false;
  @Input() expanded = true;
  @Input() drawerOpen = false;

  @Output() readonly nodeRequested = new EventEmitter<string>();
  @Output() readonly issuesRequested = new EventEmitter<string>();
  @Output() readonly toggleRequested = new EventEmitter<void>();
  @Output() readonly closeRequested = new EventEmitter<void>();

  trackEntry(_index: number, entry: WorkflowDesignerOutlineEntry): string {
    return entry.key;
  }

  requestNode(entry: WorkflowDesignerOutlineEntry): void {
    if (this.canInspect(entry)) this.nodeRequested.emit(entry.nodeId);
  }

  requestIssues(entry: WorkflowDesignerOutlineEntry): void {
    if (entry.issueCount > 0 && this.canInspect(entry))
      this.issuesRequested.emit(entry.nodeId);
  }

  private canInspect(
    entry: WorkflowDesignerOutlineEntry,
  ): entry is WorkflowDesignerOutlineEntry & { readonly nodeId: string } {
    return (
      !this.blocked &&
      this.entries.includes(entry) &&
      entry.inspectable &&
      entry.nodeId !== null
    );
  }
}
